import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { map, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { EventBusService } from '../../services/event-bus.service';
import { ReadingGroupsService } from '../../services/reading-groups.service';
import { DialogService } from 'shared/ui';
import { BookService } from '../../services/book.service';

import { SidebarContainerComponent } from '../sidebar-container/sidebar-container.component';
import { ItemMemoListComponent } from '../item-memo-list/item-memo-list.component';
import { ReadingGroupMemoListComponent } from '../reading-group-memo-list/reading-group-memo-list.component';
import { ReadingGroupMemberListDialogComponent } from '../../dialogs/reading-group-member-list-dialog/reading-group-members-dialog.component';

export type PanelType =
  | 'comment'
  | 'myAnnotationsGroup'
  | 'memberAnnotations'
  | 'myAnnotations'
  | 'commentary';

@Component({
  selector: 'viewer-right-panel',
  templateUrl: './right-panel.component.html',
  styleUrls: ['./right-panel.component.scss'],
})
export class RightPanelComponent implements OnInit, OnDestroy {
  @ViewChild(ItemMemoListComponent) memoListComp?: ItemMemoListComponent;
  @ViewChild(ReadingGroupMemoListComponent)
  readingGroupMemoListComp?: ReadingGroupMemoListComponent;

  public readonly _panels: { [key in PanelType]: string } = {
    comment: '전체 댓글',
    myAnnotationsGroup: '내 기록',
    memberAnnotations: '멤버 기록',
    myAnnotations: '내 기록',
    commentary: '코멘터리',
  };

  public _activePanel?: PanelType;
  public _memberId?: string;
  public _memberName?: string;
  public _isOwnedByUser = false; // 미리보기인지 여부

  private _unsubscriber = new Subject<void>();

  constructor(
    private _sidebarContainerComponent: SidebarContainerComponent,
    private _eventBusService: EventBusService,
    private _readingGroupsService: ReadingGroupsService,
    private _dialogService: DialogService,
    private _bookService: BookService
  ) {}

  ngOnInit(): void {
    // 읽기 모드(함께읽기/혼자읽기) 변경 감지
    this._readingGroupsService.currentGroup$
      .pipe(
        takeUntil(this._unsubscriber),
        map((group) => !!group), // 그룹 유무(모드)만 추출
        distinctUntilChanged() // 모드가 실제로 바뀔 때만 실행
      )
      .subscribe((isGroupReading) => {
        this._activePanel = isGroupReading ? 'comment' : 'myAnnotations';
      });

    this._eventBusService
      .on('ContentsComponent:bookLoad')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        this._isOwnedByUser = !!data.book.isOwnedByUser;
      });

    // 전체 댓글 탭 열기 이벤트 구독
    this._eventBusService
      .on('RightPanelComponent:openRightPanelTab')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ type }) => {
        this._activePanel = type;
        this._sidebarContainerComponent.open('right');
        this._eventBusService.fire('RightPanelComponent:opened', {
          activePanel: type,
        });
      });
  }

  ngOnDestroy(): void {
    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  /**
   * 함께읽기 모드 여부 반환
   * true: 함께읽기 (그룹)
   * false: 혼자읽기
   */
  get isGroupReading(): boolean {
    return !!this._readingGroupsService.currentGroup;
  }

  /**
   * 그룹 책인지 여부 반환
   * true: 그룹 책 (함께읽기 모드)
   * false: 나의 책 (혼자읽기 모드)
   */
  get isGroupBook(): boolean {
    return this.isGroupReading;
  }

  close(emitEvent: boolean = true): void {
    this._sidebarContainerComponent.close('right');

    emitEvent &&
      this._activePanel &&
      this._eventBusService.fire('RightPanelComponent:closed', {
        activePanel: this._activePanel,
      });
  }

  open(type: PanelType): void {
    this._activePanel = type;
    this._sidebarContainerComponent.open('right');
    this._eventBusService.fire('RightPanelComponent:opened', {
      activePanel: type,
    });
  }

  isOpened(type: PanelType): boolean {
    return (
      this._sidebarContainerComponent.isOpened('right') &&
      this._activePanel === type
    );
  }

  _onCloseButtonClick(): void {
    this.close();
  }

  _openPanel(type: PanelType): void {
    if (this._activePanel === type) {
      return;
    }

    // 멤버 기록탭의 경우 먼저 특정 맴버를 선택하는 dialog를 띄워야함.
    if (type === 'memberAnnotations') {
      const dialogRef = this._dialogService.open(
        ReadingGroupMemberListDialogComponent,
        {
          data: {
            bid: this._bookService.book!.meta.bid,
          },
        }
      );

      dialogRef.afterClosed().subscribe((result) => {
        if (!result) {
          return;
        }

        this._activePanel = type;
        this._memberId = result.memberId;
        this._memberName = result.memberName;

        this._eventBusService.fire('RightPanelComponent:opened', {
          activePanel: type,
        });
      });
    } else {
      this._activePanel = type;

      // 내 기록 탭으로 전환할 때마다 데이터 새로고침
      if (type === 'myAnnotationsGroup') {
        this._eventBusService.fire(
          'RightPanelComponent:myAnnotationsGroupOpened'
        );
      }

      this._eventBusService.fire('RightPanelComponent:opened', {
        activePanel: type,
      });
    }
  }
}
